/**
 * useRouteHead — 라우트 meta 기반 문서 제목·설명을 한 곳에서 주입한다.
 *
 * 규칙:
 *  - `meta.title` 있음 → `{title} · {brand.name}` (예: "대학 검색 · 대입로1st")
 *  - 없음(홈) → `{brand.name} · {brand.tagline}` — 브랜드 검색 결과에 홈이 브랜드명으로 노출되도록 브랜드 우선.
 *  - `meta.description` 있으면 `<meta name="description">` 도 함께(없으면 index.html 정적 기본값 유지).
 *
 * ⚠️ 페이지마다 `useHead({ title })` 를 흩뿌리지 않는다 — 누락된 화면이 전부 같은 제목으로 떨어져
 * GA4 "페이지 제목" 보고서에서 홈과 합산되고(2026-09 5.5천 조회가 한 행으로 뭉친 사고), 검색엔진은
 * 홈·자료 페이지를 중복 문서로 보고 대표 URL 을 임의로 고른다. 제목은 라우트 정의(`router/index.ts`)가 SSoT.
 *
 * 데이터 기반 head 를 자체 발행하는 페이지(약관)는 컴포넌트 `useHead` 가 이 엔트리보다 나중에
 * 등록되므로 그쪽 제목이 우선한다(unhead: 마지막 등록 승). 해당 라우트는 meta.title 을 두지 않는다.
 *
 * App.vue 에서 한 번만 호출한다(`useCanonicalHead` 와 같은 자리).
 */
import { useHead } from '@unhead/vue';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { brand } from '#/config/brand';

/** meta.title 이 없는 라우트(홈)의 문서 제목 — 브랜드명이 앞. */
export const DEFAULT_DOCUMENT_TITLE = `${brand.name} · ${brand.tagline}`;

/** 라우트 meta.title 로 문서 제목을 조립한다(브랜드 접미). */
export function buildDocumentTitle(title: string | undefined): string {
  return title ? `${title} · ${brand.name}` : DEFAULT_DOCUMENT_TITLE;
}

/** 현재 라우트의 meta.title / meta.description 을 head 에 주입한다. */
export function useRouteHead(): void {
  const route = useRoute();

  const title = computed(() => buildDocumentTitle(route.meta.title));
  const description = computed(() => route.meta.description ?? null);

  useHead({
    title,
    meta: () => (description.value ? [{ name: 'description', content: description.value }] : []),
  });
}
